/**
 * Rewrite KB source citations in LLM-produced markdown into clickable element
 * (or file) pills that show only the element name or filename. The chat
 * bubble's click delegator keys off `data-file` on the resulting span and
 * routes the click through `useOpenResource('files', filePath)` so the source
 * `.sysml` file opens in a new SPA tab.
 *
 * Supported citation forms (all produced by the lean-mode system prompt or
 * the referenced-elements trailer):
 *
 *   [Source: <path>#<ElementName>]
 *   <path>.sysml#<ElementName>          (bare, no [Source: …] wrapper)
 *   <path>.sysml                        (bare file reference, no #Name)
 *   - name: <ElementName> source: <path>
 *
 * Element-link form (when #Name is present) renders just the name; file-link
 * form (no #Name) renders just the basename. Both stash the full path in
 * `data-file` for the click handler.
 *
 * Run BEFORE `marked.parse(...)` — `marked` passes inline HTML through
 * unchanged, then DOMPurify's allowlist (extended with the data-* / role /
 * tabindex attributes used below) preserves the spans.
 */

/** Shared chip styling — matches MentionChip.tsx (composer mention pill). */
const PILL_BASE_CLASS =
  'element-ref kb-source-link inline-flex h-6 items-center gap-1 rounded-xl border border-taupe-300 bg-cream-200 px-2 text-[12px] leading-none align-middle font-medium text-taupe-900 hover:bg-cream-300 cursor-pointer focus:outline focus:outline-2 focus:outline-audi-red';

const PILL_FILE_CLASS =
  'element-ref kb-source-link inline-flex h-6 items-center gap-1 rounded-xl border border-taupe-300 bg-cream-100 px-2 text-[12px] leading-none align-middle text-taupe-700 hover:bg-cream-200 cursor-pointer focus:outline focus:outline-2 focus:outline-audi-red';

/** `[Source: <path>#<ElementName>]` — primary inline citation format. */
const BRACKET_SOURCE_RE =
  /\[Source:\s*([^\]#]+?)#([A-Za-z_][A-Za-z0-9_]*)\]/g;

/**
 * `[Source: <path>.sysml]` — bracketed citation without `#ElementName`.
 * Bedrock often emits this shape for "the whole file is the source" claims.
 * Renders as a file-link pill so the `[Source: …]` brackets are fully
 * replaced (rather than leaving stray `[Source: <pill>]` text around the
 * inner pill). Must run AFTER `BRACKET_SOURCE_RE` so the `#Name` variant
 * wins where both could match.
 */
const BRACKET_FILE_RE =
  /\[Source:\s*([^\]#\n]+?\.sysml)\s*\]/g;

/**
 * `- name: <Name> source: <path.sysml>` — trailer form from the
 * referenced-elements block. Match `name: X source: Y` anywhere (not just at
 * line start) because multiple entries can share a line. Stop the source
 * capture at another `- name:`, a newline, or end of string.
 */
const NAME_SOURCE_RE =
  /name:\s*([A-Za-z_][A-Za-z0-9_]*)\s+source:\s*([^\n]+?\.sysml)(?=\s*(?:-\s*name:|$|\n))/g;

/**
 * Bare `<path>.sysml#<ElementName>` — citation without `[Source: ... ]`
 * wrapper. First path segment (pre-first-`/`) must be space-free, which
 * prevents the lazy quantifier from gobbling preceding prose like
 * "see - knowledge-base/foo.sysml#X" as one giant match. Subsequent
 * segments may contain spaces and dashes.
 *
 * The leading `(?:^|(?<=[\s\[(>|*]))` anchor requires the match to start
 * at the beginning of input or right after whitespace/punctuation.
 * Requires at least one `/` in the path to exclude single-word `.sysml`
 * identifiers that appear in prose without context.
 *
 * Run LAST so the bracketed + `name: … source: …` forms consume first.
 */
const BARE_PATH_RE =
  /(?:^|(?<=[\s\[(>|*]))([A-Za-z0-9_][A-Za-z0-9_.:\-]*(?:[\/\\][A-Za-z0-9_.\/\\\- ]*?)?\.sysml)#([A-Za-z_][A-Za-z0-9_]*)/g;

/**
 * Bare `<path>.sysml` file reference — no `#Name` suffix. Used for
 * Source-column cells where the file itself is the salient link.
 */
const BARE_FILE_RE =
  /(?:^|(?<=[\s\[(>|*]))([A-Za-z0-9_][A-Za-z0-9_.:\-]*(?:[\/\\][A-Za-z0-9_.\/\\\- ]*?)?\.sysml)(?![#A-Za-z0-9_])/g;

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function basename(filePath: string): string {
  const trimmed = filePath.trim();
  const lastSep = Math.max(trimmed.lastIndexOf('/'), trimmed.lastIndexOf('\\'));
  return lastSep >= 0 ? trimmed.slice(lastSep + 1) : trimmed;
}

function renderElementLink(name: string, filePath: string): string {
  const trimmed = filePath.trim();
  const file = escapeAttr(trimmed);
  const elem = escapeAttr(name);
  const title = escapeAttr(`${trimmed}#${name}`);
  return `<span class="${PILL_BASE_CLASS}" part="element-ref" tabindex="0" role="link" aria-label="Open ${elem} in a new tab" data-element="${elem}" data-file="${file}" title="${title}">${elem}</span>`;
}

function renderFileLink(filePath: string): string {
  const trimmed = filePath.trim();
  const file = escapeAttr(trimmed);
  const displayName = escapeAttr(basename(trimmed));
  const title = escapeAttr(trimmed);
  return `<span class="${PILL_FILE_CLASS}" part="element-ref" tabindex="0" role="link" aria-label="Open ${displayName} in a new tab" data-file="${file}" title="${title}">${displayName}</span>`;
}

/**
 * Pre-rendered `<span class="… kb-source-link …" data-file="X" …>Display</span>`
 * literal that the LLM sometimes echoes back from prior turns. Unwrap to a
 * bare `path` or `path#Name` citation so the downstream rewriter re-renders
 * it with the current pill styling.
 */
const LITERAL_SPAN_RE =
  /<span\s+[^>]*?kb-source-link[^>]*?data-file=["']([^"']+)["'][^>]*>([A-Za-z0-9_][^<]*?)<\/span>/g;
const LITERAL_SPAN_ESCAPED_RE =
  /&lt;span\s+[^&]*?kb-source-link[^&]*?data-file=(?:&quot;|")([^&"']+)(?:&quot;|")[^&]*?&gt;([A-Za-z0-9_][^&<]*?)&lt;\/span&gt;/g;

/**
 * Any residual `<span>` / `</span>` tag (raw or entity-escaped) that survives
 * the kb-source-link unwrap. The LLM should never emit `<span>` — drop the
 * tags and keep the inner text so the BARE_* passes can re-link any plain
 * citations.
 */
const RESIDUAL_SPAN_RE = /<\/?span\b[^>]*>/gi;
const RESIDUAL_SPAN_ESCAPED_RE = /&lt;\/?span\b[^<>]*?&gt;/gi;

/**
 * Fenced code block delimited by triple backticks (with optional language
 * tag). The whole block — including the opening + closing fences — is
 * stashed so no citation pass can re-link path text the user wrapped as a
 * code sample. Multi-line via [\s\S]; non-greedy body avoids slurping two
 * fences into one match.
 */
const FENCED_CODE_RE = /```[^\n]*\n[\s\S]*?\n```/g;

/**
 * Inline backtick code span — single backticks, single-line. The fenced
 * pass already stashed triple-fence blocks, so a bare backtick here is
 * always an inline-code delimiter.
 */
const INLINE_CODE_RE = /`[^`\n]+?`/g;

/**
 * Transform all supported citation forms in `markdown` into inline element /
 * file pills. Call BEFORE markdown parsing.
 *
 * Each pass stashes the rendered HTML behind a sentinel token and substitutes
 * the tokens back at the end. Without this, a later pass could re-match the
 * `.sysml#Name` substring inside an earlier pass's injected `title=` /
 * `data-file=` attribute.
 *
 * Code spans (single backticks) and fenced code blocks (triple backticks)
 * are stashed BEFORE any citation pass runs. Without this, a chip span would
 * land inside `<code>...</code>` after markdown parsing and the HTML encoder
 * would surface the raw `<span class="element-ref ...">` tag to the user.
 */
export function rewriteKbSourceCitations(markdown: string): string {
  const codeSlots: string[] = [];
  const stashCode = (literal: string): string => {
    const token = ` KBCODE${codeSlots.length} `;
    codeSlots.push(literal);
    return token;
  };

  let out = markdown.replace(FENCED_CODE_RE, (m: string) => stashCode(m));
  out = out.replace(INLINE_CODE_RE, (m: string) => stashCode(m));

  const slots: string[] = [];
  const stash = (html: string): string => {
    const token = ` KBLINK${slots.length} `;
    slots.push(html);
    return token;
  };

  const unwrapSpan = (file: string, display: string): string =>
    display.includes('.sysml') ? file : `${file}#${display}`;
  out = out.replace(LITERAL_SPAN_RE, (_m, file: string, display: string) =>
    unwrapSpan(file, display),
  );
  out = out.replace(LITERAL_SPAN_ESCAPED_RE, (_m, file: string, display: string) =>
    unwrapSpan(file, display),
  );

  out = out.replace(RESIDUAL_SPAN_RE, '');
  out = out.replace(RESIDUAL_SPAN_ESCAPED_RE, '');

  out = out.replace(BRACKET_SOURCE_RE, (_m, rawPath: string, name: string) =>
    stash(renderElementLink(name, rawPath)),
  );
  out = out.replace(BRACKET_FILE_RE, (_m, rawPath: string) =>
    stash(renderFileLink(rawPath)),
  );
  out = out.replace(NAME_SOURCE_RE, (_m, name: string, rawPath: string) =>
    stash(renderElementLink(name, rawPath)),
  );
  out = out.replace(BARE_PATH_RE, (_m, rawPath: string, name: string) =>
    stash(renderElementLink(name, rawPath)),
  );
  out = out.replace(BARE_FILE_RE, (_m, rawPath: string) =>
    stash(renderFileLink(rawPath)),
  );

  out = out.replace(/ KBLINK(\d+) /g, (_m, idx: string) =>
    slots[Number(idx)] ?? '',
  );

  return out.replace(/ KBCODE(\d+) /g, (_m, idx: string) =>
    codeSlots[Number(idx)] ?? '',
  );
}